import React from 'react';
import {
  $addProgramInput,
  AccountAttributes,
  AddProgramInput,
  ProgramAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  MenuItem,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DialogClose, QlfFormRow } from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SYSTEM_LANGUAGES } from '@loyalty/shared-common';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { usePrograms } from '../../ProgramsContext';

interface Props extends DialogProps {
  enabled: boolean;
  account: AccountAttributes;
  program: ProgramAttributes | undefined;
  onSubmit: (values: any) => void;
  onClose: () => void;
}

const useStyles = makeStyles<Theme>(({ spacing, palette, shape }) => ({
  title: {
    padding: spacing(4, 8, 1),
    textAlign: 'center',
    '&:after': {
      backgroundColor: palette.primary.main,
      content: "''",
      display: 'block',
      height: 3,
      margin: spacing(1, 'auto', 0),
      width: 40,
    },
  },
  content: {
    padding: spacing(1, 8, 0.5),
    '&:first-of-type': {
      paddingTop: spacing(3),
    },
  },
  actions: {
    padding: spacing(0.5, 8, 4),
    justifyContent: 'flex-end',
  },
  closeButton: {
    fontSize: 16,
    padding: spacing(1.5),
    position: 'absolute',
    right: 0,
    top: 0,
  },
  field: {
    marginBottom: spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  languageInput: {
    minHeight: `calc(40px - ${spacing(0.4)}px)`,
    padding: spacing(0.2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    borderRadius: shape.borderRadius,
    margin: 2,
  },
  chipAvatar: {
    height: 10,
    width: 10,
  },
}));

export function LoyaltyProgramDialog({
  enabled,
  account,
  program,
  onSubmit,
  onClose,
  open,
  ...props
}: Props): React.ReactElement {
  const { t } = useTranslation();
  const isNew = !program?.id;
  const classes = useStyles();
  const { isLoading } = usePrograms();

  const resolver = useJoiResolver($addProgramInput);
  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, isSubmitting, errors },
  } = useForm<AddProgramInput>({
    mode: 'onChange',
    defaultValues: program || {
      name: '',
      languages: [],
    },
    resolver,
  });
  let timezones: string[] = [];
  if ((Intl as any).supportedValuesOf) {
    timezones = (Intl as any).supportedValuesOf('timeZone');
  }
  const languagesValue = watch('languages', program?.languages) || [];
  const timezoneValue =
    watch('timezone', program?.timezone) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'Europe/Brussels';
  const loading = isLoading('addProgram') || isLoading('updateProgram');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth {...props}>
      <DialogTitle className={classes.title}>
        {isNew ? t('loyalty.dialog.title.create') : t('loyalty.dialog.title.update')}
        <DialogClose close={onClose} />
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent className={classes.content}>
          <Controller
            control={control}
            name="name"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfFormRow label={t('loyalty.dialog.properties.name')} mandatory>
                <TextField
                  {...field}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  placeholder={t('loyalty.dialog.properties.name')}
                  id={'name-dialog-id'}
                />
              </QlfFormRow>
            )}
          />

          <Controller
            control={control}
            name="publicName"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfFormRow label={t('loyalty.dialog.properties.public-name')}>
                <TextField
                  {...field}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  placeholder={t('loyalty.dialog.properties.public-name')}
                  id={'public_name-dialog-id'}
                />
              </QlfFormRow>
            )}
          />

          <Controller
            control={control}
            name="externalRef"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfFormRow label={t('loyalty.dialog.properties.externalRef')}>
                <TextField
                  {...field}
                  inputRef={ref}
                  error={!!error}
                  helperText={error?.message}
                  id={'exter-ref-id'}
                />
              </QlfFormRow>
            )}
          />

          <Controller
            control={control}
            name="languages"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfFormRow label={t('loyalty.dialog.properties.languages')} mandatory>
                <TextField
                  {...field}
                  id={'languages-select-id'}
                  error={!!error}
                  helperText={error?.message}
                  select
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected: any) => (
                      <div className={classes.chips}>
                        {selected.map((value: string) => (
                          <Chip
                            key={value}
                            label={
                              Object.values(SYSTEM_LANGUAGES).find(
                                ({ isoCode }) => isoCode === value,
                              )?.name
                            }
                            className={classes.chip}
                            avatar={
                              <img
                                src={`${BASE_PATH}/assets/template/assets/flags/${value}.svg`}
                                alt={value}
                              />
                            }
                          />
                        ))}
                      </div>
                    ),
                  }}
                >
                  {Object.values(SYSTEM_LANGUAGES).map(({ isoCode, name }) => (
                    <MenuItem key={name} value={isoCode}>
                      <Checkbox checked={languagesValue?.indexOf(isoCode) > -1} />
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </QlfFormRow>
            )}
          />
          <Controller
            control={control}
            name="timezone"
            defaultValue={timezoneValue}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <QlfFormRow label={t('loyalty.dialog.properties.timezone')} mandatory>
                <TextField
                  {...field}
                  id={'timezone-select-id'}
                  error={!!error}
                  helperText={error?.message}
                  select
                  disabled={!isNew}
                >
                  {Object.values(timezones).map(timezone => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </TextField>
              </QlfFormRow>
            )}
          />
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Tooltip title={enabled ? '' : t('shared.action.permission.required')}>
            <div>
              <Button
                color="green"
                disabled={!isDirty || !enabled || loading}
                type="submit"
                id={'create-button-id'}
              >
                {loading && (
                  <FontAwesomeIcon
                    fontSize="inherit"
                    icon={faSpinner}
                    spin={true}
                    style={{ marginRight: '10px' }}
                  />
                )}
                {isNew
                  ? t('loyalty.dialog.confirm-button.create')
                  : t('loyalty.dialog.confirm-button.update')}
              </Button>
            </div>
          </Tooltip>
        </DialogActions>
      </form>
    </Dialog>
  );
}
